<template>
  <div class="scan-loading-order-container">
    <!-- 头部导航 -->
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">装车单管理</h1>
      <button @click="handleAddOrder" class="btn add-btn">新增装车单</button>
    </header>

    <!-- 主内容区 -->
    <main class="content">
      <div class="form-section source-section">
        <h3 class="section-title">装车订单</h3>

        <!-- 状态提示横幅 -->
        <div v-if="statusMsg" class="status-banner" :class="statusType">
          {{ statusMsg }}
        </div>

        <!-- 加载中状态 -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>
        
        <!-- 错误提示 -->
        <div v-if="errorMsg" class="error-message">{{ errorMsg }}</div>

        <!-- 空状态 -->
        <div v-if="orderList.length === 0 && !loading && !errorMsg" class="empty-state">
          <div class="empty-icon">📄</div>
          <p class="empty-text">暂无装车订单</p>
          <button @click="handleAddOrder" class="btn empty-action-btn">立即创建</button>
        </div>

        <!-- 装车单列表 -->
        <div class="card-list" v-else-if="!loading && !errorMsg">
          <div class="data-card" v-for="item in orderList" :key="item.id" @click="navigateToDetail(item.id)">
            <div class="card-header">
              <span class="card-label">装车单号 {{ item.orderNo }}</span>
              <span class="card-status" :class="getStatusClass(item.status)">{{ getStatusText(item.status) }}</span>
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="row-label">创建时间：</span>
                <span class="row-value">{{ formatDate(item.createTime) }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">主机厂：</span>
                <span class="row-value">{{ item.factoryName || '-' }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">车次：</span>
                <span class="row-value">第{{ item.vehicleSequence || '-' }}车</span>
              </div>
              <div class="card-row">
                <span class="row-label">车牌号：</span>
                <span class="row-value">{{ item.vehiclePlate || '-' }}</span>
              </div>
            </div>
            <div class="card-footer">
              <button 
                @click="handleEnterOrder(item.id, $event)" 
                class="btn enter-order-btn"
                :disabled="item.status === 2 || item.status === 3"
              >
                {{ item.status === 0 ? '开始装车' : item.status === 2 ? '继续装车' : '查看详情' }}
              </button>
            </div>
          </div>
        </div>

        <!-- 分页控件 - 适配PDA触控操作 -->
        <div v-if="total > 0 && !loading && !errorMsg" class="pagination-container">
          <div class="pagination-info">
            第 {{ currentPage }} 页 / 共 {{ totalPages }} 页
          </div>
          <div class="pagination-buttons">
            <button 
              class="pagination-btn" 
              @click="handlePageChange(1)"
              :disabled="currentPage === 1 || loading"
            >
              首页
            </button>
            <button 
              class="pagination-btn" 
              @click="handlePageChange(currentPage - 1)"
              :disabled="currentPage === 1 || loading"
            >
              上一页
            </button>
            <button 
              class="pagination-btn" 
              @click="handlePageChange(currentPage + 1)"
              :disabled="currentPage === totalPages || loading"
            >
              下一页
            </button>
            <button 
              class="pagination-btn" 
              @click="handlePageChange(totalPages)"
              :disabled="currentPage === totalPages || loading"
            >
              末页
            </button>
          </div>
          <div class="page-size-selector">
            <select 
              v-model="pageSize" 
              class="form-control"
              @change="handlePageSizeChange"
            >
              <option value="5">5条/页</option>
              <option value="10">10条/页</option>
              <option value="20">20条/页</option>
              <option value="50">50条/页</option>
            </select>
          </div>
        </div>
      </div>
    </main>

    <!-- 新增装车单弹窗 -->
    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">新增装车单</h3>
          <button @click="showAddModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <!-- 主机厂下拉选择框 -->
          <div class="form-group">
            <label for="factoryId">主机厂 <span class="required">*</span></label>
            <select 
              id="factoryId"
              v-model="selectedFactoryId"
              class="form-control"
              @change="handleFactoryChange"
            >
              <option value="">请选择主机厂</option>
              <option v-for="factory in factories" :key="factory.id" :value="factory.id">
                {{ factory.factoryName }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showAddModal = false" class="btn modal-cancel">取消</button>
          <button @click="createOrder" class="btn modal-confirm" :disabled="loading || !selectedFactoryId">
            {{ loading ? '创建中...' : '创建装车单' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getLoadingOrderList, createLoadingOrder } from '@/api/loadingOrder'
import { getFactories } from '@/api/stockOut'
import { getUicAuthUserInfo } from '@/utils/auth'

export default {
  name: 'LoadingOrderList',
  setup() {
    const router = useRouter()
    
    // 状态管理
    const orderList = ref([])
    const loading = ref(false)
    const errorMsg = ref('')
    const statusMsg = ref('')
    const statusType = ref('success')
    const showAddModal = ref(false)
    const suppliers = ref([])
    const selectedSuppliers = ref([])
    const productionOrderNo = ref('')
    const vehiclePlate = ref('')
    const factories = ref([])
    const selectedFactoryId = ref('')
    const selectedFactoryName = ref('')
    const isFactoriesLoaded = ref(false) // 新增标记，防止重复加载

    // 分页相关状态
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const totalPages = ref(0)

    // 获取装车单列表（带分页）
    const fetchOrderList = async () => {
      try {
        loading.value = true
        errorMsg.value = ''
        const res = await getLoadingOrderList({ 
          current: currentPage.value, 
          size: pageSize.value 
        })
        
        orderList.value = res.data.records || []
        total.value = res.data.total || 0
        totalPages.value = Math.ceil(total.value / pageSize.value)
        
        if (orderList.value.length === 0) {
          statusMsg.value = '当前页暂无装车记录'
          statusType.value = 'info'
        } else {
          statusMsg.value = ''
        }
      } catch (err) {
        errorMsg.value = '获取装车单列表失败，请稍后重试'
        console.error(err)
      } finally {
        loading.value = false
      }
    }


    // 获取主机厂列表
    const fetchFactories = async () => {
      // 如果已经加载过，不再重复请求
      if (isFactoriesLoaded.value) return
      
      try {
        // 获取用户信息，提取deptId
        const userInfo = getUicAuthUserInfo()
        const deptId = userInfo?.deptId || ''
        
        if (!deptId) {
          console.warn('未获取到用户的部门ID')
          setStatus('获取部门信息失败', 'error')
          return
        }
        
        // 注意：根据后端接口要求，参数名应该是 bizDept 而不是 deptId
        const res = await getFactories({ current: 1, size: 20, bizDept: deptId })
        
        // 检查返回的数据结构，确保正确提取主机厂列表
        if (res && res.data && Array.isArray(res.data)) {
          factories.value = res.data
        } else if (res && res.data && res.data.records) {
          factories.value = res.data.records
        } else {
          factories.value = []
        }
        
        isFactoriesLoaded.value = true // 标记为已加载
      } catch (err) {
        console.error('获取主机厂失败', err)
        setStatus('获取主机厂列表失败', 'error')
      }
    }

    // 创建装车单 - 修改API调用，移除车牌号参数
    const createOrder = async () => {
      try {
        loading.value = true
        const res = await createLoadingOrder({
          // 移除车牌号参数
          factoryId: selectedFactoryId.value,
          factoryName: selectedFactoryName.value
        })
        
        if (res && (res.success === true || res.code === 200)) {
          setStatus(res.msg || res.data || '装车单创建成功', 'success')
          showAddModal.value = false
          // 重置表单
          vehiclePlate.value = ''
          selectedFactoryId.value = ''
          selectedFactoryName.value = ''
          // 重新加载列表数据（回到第一页）
          currentPage.value = 1
          fetchOrderList()
        } else {
          setStatus(res.msg || '创建装车单失败', 'error')
        }
      } catch (err) {
        setStatus('创建装车单失败，请稍后重试', 'error')
        console.error(err)
      } finally {
        loading.value = false
      }
    }

    // 设置状态提示
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      setTimeout(() => {
        if (statusMsg.value === message) statusMsg.value = ''
      }, 3000)
    }

    // 处理进入装车单
    const handleEnterOrder = async (id, event) => {
      event.stopPropagation()
      try {
        loading.value = true
        router.push({
          path: `/loading-order/detail/${id}`
        })
      } catch (err) {
        console.error('进入装车单详情失败', err)
        setStatus('进入装车单详情失败，请稍后重试', 'error')
      } finally {
        loading.value = false
      }
    }

    // 获取状态文本
    const getStatusText = (status) => {
      const statusMap = {
        0: '待装车',
        1: '已完成',
        2: '已取消'
      }
      return statusMap[status] || '未知状态'
    }

    // 主机厂选择变化处理
    const handleFactoryChange = () => {
      if (selectedFactoryId.value) {
        const selectedFactory = factories.value.find(factory => factory.id === selectedFactoryId.value);
        if (selectedFactory) {
          selectedFactoryName.value = selectedFactory.factoryName || '';
        } else {
          selectedFactoryName.value = '';
        }
      } else {
        selectedFactoryName.value = '';
      }
    }

    // 分页相关方法
    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages.value || page === currentPage.value) {
        return
      }
      currentPage.value = page
      fetchOrderList()
    }

    const handlePageSizeChange = () => {
      currentPage.value = 1 // 改变每页条数时回到第一页
      fetchOrderList()
    }

    // 页面初始化 - 只加载订单列表，不加载主机厂列表
    onMounted(() => {
      fetchOrderList()
      // 移除原有的fetchFactories()调用
    })

    // 点击新增装车单按钮时加载主机厂列表并显示弹窗
    const handleAddOrder = async () => {
      await fetchFactories() // 点击时才加载主机厂列表
      showAddModal.value = true
    }

    // 供应商选择切换
    const toggleSupplierSelection = (supplierId) => {
      if (selectedSuppliers.value.includes(supplierId)) {
        selectedSuppliers.value = selectedSuppliers.value.filter(id => id !== supplierId);
      } else {
        selectedSuppliers.value.push(supplierId);
      }
    };

    return {
      orderList,
      loading,
      errorMsg,
      statusMsg,
      statusType,
      showAddModal,
      suppliers,
      selectedSuppliers,
      productionOrderNo,
      vehiclePlate,
      factories,
      selectedFactoryId,
      selectedFactoryName,
      
      // 分页相关
      currentPage,
      pageSize,
      total,
      totalPages,
      
      // 方法
      handleBack: () => router.go(-1),
      handleAddOrder,
      navigateToDetail: (id) => router.push(`/loading-order/detail/${id}`),
      createOrder,
      formatDate: (date) => {
        if (!date) return ''
        return new Date(date).toLocaleString()
      },
      getStatusClass: (status) => `status-${status}`,
      getStatusText,
      handleEnterOrder,
      toggleSupplierSelection,
      handleFactoryChange,
      handlePageChange,
      handlePageSizeChange
    }
  }
}
</script>

<style scoped>
/* 基础容器样式 */
.scan-loading-order-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: #f5f7fa;
  color: #333;
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px 16px;
  background-color: #032566;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.icon-back {
  width: 24px;
  height: 24px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 新增装车单按钮样式 */
.add-btn {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  min-height: 40px;
  margin-left: auto;
  transition: background-color 0.2s;
}

.add-btn:hover {
  background-color: #359469;
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 20px;
}

/* 表单区域样式 */
.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: #1890ff;
  margin-right: 8px;
  border-radius: 2px;
}

/* 状态提示样式 */
.status-banner {
  padding: 12px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  transition: opacity 0.3s;
}

.status-banner.success {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #e6f7d9;
}

.status-banner.error {
  background-color: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffccc7;
}

.status-banner.warn {
  background-color: #fff7e6;
  color: #faad14;
  border: 1px solid #ffe8b6;
}

.status-banner.info {
  background-color: #e6f7ff;
  color: #1890ff;
  border: 1px solid #bae7ff;
}

/* 加载中状态 */
.loading-state {
  padding: 48px 0;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: #666;
  font-size: 14px;
}

/* 错误提示文本 */
.error-message {
  color: #f5222d;
  font-size: 14px;
  margin: 0 0 8px 0;
  text-align: center;
  padding: 12px;
}

/* 空状态样式 */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.4;
}

.empty-text {
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 24px;
}

.empty-action-btn {
  background-color: #1890ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.empty-action-btn:hover {
  background-color: #096dd9;
}

/* 装车单列表卡片样式 */
.card-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px; /* 为分页控件留出空间 */
}

.data-card {
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #f0f2f5;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  cursor: pointer;
}

.data-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.card-header {
  background-color: #f9fafb;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-label {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.card-status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

/* 状态标签样式 */
.status-0 { /* 待装车 */
  background-color: #fff7e6;
  color: #faad14;
}

.status-1 { /* 装车中 */
  background-color: #e6f7ff;
  color: #1890ff;
}

.status-2 { /* 已完成 */
  background-color: #f6ffed;
  color: #52c41a;
}

.status-3 { /* 已取消 */
  background-color: #fff1f0;
  color: #f5222d;
}

.card-body {
  padding: 16px;
}

.card-row {
  display: flex;
  margin-bottom: 12px;
  line-height: 1.5;
}

.card-row:last-child {
  margin-bottom: 0;
}

.row-label {
  color: #666;
  width: 100px;
  flex-shrink: 0;
}

.row-value {
  color: #333;
  flex: 1;
}

.card-footer {
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
  display: flex;
  justify-content: flex-end;
}

.enter-order-btn {
  background-color: #1890ff;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.enter-order-btn:hover:not(:disabled) {
  background-color: #096dd9;
}

.enter-order-btn:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

/* 分页控件样式 - 适配PDA触控 */
.pagination-container {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #f0f2f5;
  margin-top: 12px;
}

.pagination-info {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
  width: 100%;
  text-align: center;
}

.pagination-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
  width: 100%;
  margin-bottom: 8px;
}

.pagination-btn {
  background-color: #f5f7fa;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  min-width: 80px;
  min-height: 44px; /* 增大点击区域，适配PDA触控 */
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #eef0f3;
  border-color: #c9cdcf;
}

.pagination-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #f5f7fa;
  border-color: #e4e7ed;
}

.page-size-selector {
  width: 100%;
  text-align: center;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  backdrop-filter: blur(2px);
}

.modal-container {
  width: 100%;
  max-width: 500px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  animation: modalFadeIn 0.3s;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #999;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s, color 0.2s;
}

.modal-close:hover {
  background-color: #f5f7fa;
  color: #333;
}

.modal-body {
  padding: 16px;
  max-height: 70vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #f0f2f5;
}

.modal-footer .btn {
  flex: 1;
  min-height: 44px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: background-color 0.2s;
}

.modal-cancel {
  background-color: #f5f7fa;
  color: #666;
  border: 1px solid #dcdfe6;
}

.modal-cancel:hover {
  background-color: #eef0f3;
}

.modal-confirm {
  background-color: #1890ff;
  color: #fff;
}

.modal-confirm:disabled {
  background-color: #a0cfff;
  cursor: not-allowed;
}

.modal-confirm:not(:disabled):hover {
  background-color: #096dd9;
}

/* 表单控件样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 44px; /* 增大触控区域 */
}

.form-control:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.form-hint {
  margin: 6px 0 0 0;
  color: #999;
  font-size: 12px;
  line-height: 1.4;
}

/* 供应商列表样式 */
.supplier-list {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
}

.supplier-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.supplier-item:hover {
  background-color: #f5f7fa;
}

.supplier-item.selected {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
}

.supplier-info {
  flex: 1;
}

.supplier-name {
  font-weight: 500;
  color: #333;
}

.supplier-code {
  font-size: 12px;
  color: #666;
}

/* 自定义复选框样式 */
.checkbox {
  width: 18px;
  height: 18px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  position: relative;
}

.checkbox.checked {
  background-color: #1890ff;
  border-color: #1890ff;
}

.checkbox.checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 14px;
  top: -2px;
  left: 3px;
}

/* 加载动画 */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式适配 - PDA设备优化 */
@media (max-width: 768px) {
  .header {
    padding: 3px 8px;
  }
  
  .add-btn {
    padding: 6px 12px;
    font-size: 13px;
  }
  
  .card-list {
    gap: 8px;
  }
  
  .card-header, .card-body, .card-footer {
    padding: 10px 12px;
  }
  
  .card-label {
    font-size: 14px;
  }
  
  .modal-body {
    padding: 12px;
  }
  
  .modal-footer {
    padding: 8px 12px;
  }
  
  .modal-footer .btn {
    padding: 8px 12px;
    font-size: 14px;
  }

  /* 分页控件在小屏幕上的优化 */
  .pagination-container {
    flex-direction: column;
    gap: 10px;
  }
  
  .pagination-buttons {
    width: 100%;
    flex-wrap: wrap;
  }
  
  .pagination-btn {
    flex: 1;
    min-width: auto;
    padding: 10px 5px;
    font-size: 13px;
  }
  
  .page-size-selector {
    width: 100%;
  }
}
</style>
